import React, { memo, useState, useEffect, useMemo, useCallback } from 'react';
import { Button, Select, Space, Tooltip, Modal } from 'antd';
import {
  ReloadOutlined,
  DownloadOutlined,
  ExpandOutlined,
  CompressOutlined,
  CloseOutlined,
  HolderOutlined,
} from '@ant-design/icons';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import GeocollectMembersSelector from '~/components/app_common/GeocollectMembersSelector/GeocollectMembersSelector';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  projectMembersWithRolesAtom,
  projectMembersWithRolesAsyncAtom,
  dispatchZoomToMember,
} from '~/components/Map/Member/MemberStore';
import { currentProjectAtom } from '~/stores/ProjectStore';
import { ProfileAPIs } from '~/common/typescript/apis/ProfileAPIs';
import {
  HiOutlineChartBar,
  HiOutlineUsers,
  HiOutlineEye,
  HiOutlineFilter,
  HiOutlineTrendingUp,
  HiOutlineChartPie,
  HiOutlineClock,
} from 'react-icons/hi';
import { FaTriangleExclamation } from 'react-icons/fa6';
import { PiTrendUp, PiChartBar, PiTrendDown } from 'react-icons/pi';

import { CollectDashboardData, CollectTimeRange } from '~/common/typescript/models/CollectDashboard';
import { CollectDashboardAPIs } from '~/common/typescript/apis/CollectDashboardAPIs';
import DashboardWidgetSettingsPopover, {
  DEFAULT_DASHBOARD_WIDGETS,
  DashboardWidgetConfigItem,
  DashboardWidgetKey,
} from './DashboardWidgetSettingsPopover';
import {
  DailyCollectionTrendChart,
  StaffContributionDonutChart,
  GeometryAndHourlyChart,
} from './DashboardCharts';
import {
  UI_Container,
  UI_Header,
  UI_HeaderLeft,
  UI_HeaderRight,
  UI_Title,
  UI_Filters,
  UI_FilterGroup,
  UI_FilterLabel,
  UI_DashboardContent,
  UI_CardTitle,
  UI_CardHeaderRow,
  UI_WidgetHoverControls,
  UI_WidgetSortableItem,
  UI_WidgetCard,
  UI_WidgetDragHandle,
  UI_WidgetCloseBtn,
  UI_EmptyWidgetsState,
  UI_EmptyWidgetsText,
  UI_KPIGrid,
  UI_KPIItem,
  UI_KPIValue,
  UI_KPILabel,
  UI_Sparkline,
  UI_ProgressList,
  UI_ProgressItem,
  UI_ProgressInfo,
  UI_ProgressName,
  UI_ProgressStats,
  UI_ProgressBar,
  UI_ProgressFill,
  UI_StaffTable,
  UI_StaffHeader,
  UI_StaffRow,
  UI_StaffRecordsHeader,
  UI_StaffRecordsValue,
  UI_HeatmapCard,
  UI_Heatmap,
  UI_HeatmapTable,
  UI_HeatmapCell,
  UI_ErrorsCard,
  UI_ErrorsList,
  UI_ErrorItem,
  UI_ErrorTime,
  UI_ErrorDetails,
  UI_ErrorUser,
  UI_ErrorForm,
  UI_ErrorNote,
  UI_TopCardsContainer,
  UI_BottomCardsContainer,
  UI_MobileFilterButton,
} from './DashboardTab.styles';

interface SortableWidgetCardProps {
  widgetKey: DashboardWidgetKey;
  title: string;
  icon: React.ReactNode;
  onHide: (key: DashboardWidgetKey) => void;
  children: React.ReactNode;
}

const SortableWidgetCard = memo(({
  widgetKey,
  title,
  icon,
  onHide,
  children,
}: SortableWidgetCardProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: widgetKey });

  const { role: _ignoredRole, ...cleanAttributes } = attributes;

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 20 : 1,
    opacity: isDragging ? 0.92 : 1,
  };

  return (
    <UI_WidgetSortableItem
      ref={setNodeRef}
      style={style}
      $isDragging={isDragging}
      data-testid={`dashboard-widget-${widgetKey}`}
    >
      <UI_WidgetCard $isDragging={isDragging}>
        <UI_CardHeaderRow>
          <UI_CardTitle>
            {icon}
            {title}
          </UI_CardTitle>
          <UI_WidgetHoverControls
            className="widget-hover-controls"
            $isDragging={isDragging}
            data-testid={`widget-hover-controls-${widgetKey}`}
          >
            <Tooltip title="Kéo thả để thay đổi vị trí widget">
              <UI_WidgetDragHandle
                type="button"
                {...cleanAttributes}
                {...listeners}
                $isDragging={isDragging}
                aria-label={`Kéo thả hoặc dùng phím mũi tên để thay đổi vị trí widget ${title}`}
                data-testid={`widget-drag-handle-${widgetKey}`}
              >
                <HolderOutlined />
                <span>Kéo thả</span>
              </UI_WidgetDragHandle>
            </Tooltip>
            <Tooltip title="Ẩn widget này">
              <UI_WidgetCloseBtn
                type="button"
                aria-label={`Ẩn widget ${title}`}
                data-testid={`widget-hide-btn-${widgetKey}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onHide(widgetKey);
                }}
              >
                <CloseOutlined />
              </UI_WidgetCloseBtn>
            </Tooltip>
          </UI_WidgetHoverControls>
        </UI_CardHeaderRow>
        {children}
      </UI_WidgetCard>
    </UI_WidgetSortableItem>
  );
});

interface DashboardTabProps {
  tableHeight: string;
  onMaximize: () => void;
  onClose: () => void;
  isMaximized: boolean;
  projectId?: number;
  onFormSelect?: (formUuid: string, formData?: any) => void;
}

const DashboardTab = ({ tableHeight, onMaximize, onClose, isMaximized, projectId, onFormSelect }: DashboardTabProps) => {
  const memberWithRoles = useAtomValue(projectMembersWithRolesAtom);
  const currentProject = useAtomValue(currentProjectAtom);
  const fetchMembers = useSetAtom(projectMembersWithRolesAsyncAtom);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedForm, setSelectedForm] = useState<string>('all');
  const [selectedCollector, setSelectedCollector] = useState<number>(-1); // Initialize with -1 for "All"
  const [timeRange, setTimeRange] = useState<CollectTimeRange>('all');
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [drillDownView, setDrillDownView] = useState<{ type: 'form' | 'collector' | null; id: string | number | null }>(
    { type: null, id: null },
  );

  const [dashboardData, setDashboardData] = useState<CollectDashboardData>({
    overview: {
      totalRecords: 0,
      completionRate: 0,
      errorRate: -1,
      trends: {
        totalRecords: [],
        completionRate: [],
        errorRate: [],
      },
    },
    formsProgress: [],
    staffPerformance: [],
    errorHeatmap: {
      forms: [],
      fields: [],
      data: [],
    },
    recentErrors: [],
  });
  const [loading, setLoading] = useState(true);
  const [nameLookup, setNameLookup] = useState<Record<number, string>>({});
  const [customDateRange, setCustomDateRange] = useState<[string, string] | null>(null); // [startDate, endDate]

  // Fetch dashboard overview data on component mount and when filters change
  useEffect(() => {
    const fetchDashboardData = async () => {
      // Only fetch if projectId is available
      if (projectId === undefined || projectId === null) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        // Create filters object based on current state
        const filters: Partial<CollectDashboardFilters> = {
          formId: selectedForm !== 'all' ? selectedForm : undefined,
          collectorId: selectedCollector !== -1 ? selectedCollector.toString() : undefined,
          projectId: projectId,
        };

        // Only add timeRange to filters if it's not 'all'
        if (timeRange !== 'all') {
          filters.timeRange = timeRange;
        }

        // Fetch all dashboard data in parallel using the combined endpoint
        const data = await CollectDashboardAPIs.remote_getCollectDashboardData(filters);
        setDashboardData(data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [selectedForm, selectedCollector, timeRange, projectId]);

  // Ensure project members are loaded when DashboardTab mounts
  useEffect(() => {
    if (projectId && memberWithRoles.length === 0) {
      fetchMembers();
    }
  }, [projectId, memberWithRoles.length, fetchMembers]);

  // Create name lookup map when members or project owner change
  useEffect(() => {
    setNameLookup((prev) => {
      const lookup: Record<number, string> = { ...prev };
      if (currentProject?.owner?.id) {
        lookup[currentProject.owner.id] =
          currentProject.owner.fullname || currentProject.owner.username || `User ${currentProject.owner.id}`;
      }
      memberWithRoles.forEach((m: any) => {
        if (m.user?.id) {
          lookup[m.user.id] = m.user.fullname || m.user.username || `User ${m.user.id}`;
        }
      });
      return lookup;
    });
  }, [memberWithRoles, currentProject?.owner]);

  // Resolve any collector user IDs in staffPerformance / recentErrors not yet present in nameLookup (e.g., project owner or removed members)
  useEffect(() => {
    const unknownIds = new Set<number>();
    dashboardData.staffPerformance.forEach((staff) => {
      const uid = staff.userId || parseInt(staff.name?.replace('User ', '') || '0', 10);
      if (uid > 0 && !nameLookup[uid]) {
        unknownIds.add(uid);
      }
    });
    dashboardData.recentErrors.forEach((error) => {
      const uid = error.userId || parseInt(error.collector?.replace('User ', '') || '0', 10);
      if (uid > 0 && !nameLookup[uid]) {
        unknownIds.add(uid);
      }
    });

    if (unknownIds.size === 0) return;

    let cancelled = false;
    Promise.all(
      Array.from(unknownIds).map(async (uid) => {
        try {
          const profile = await ProfileAPIs.remote_getProfile(uid);
          if (profile) {
            return { uid, name: profile.fullname || profile.username || `User ${uid}` };
          }
        } catch {
          // ignore fallback profile fetch error
        }
        return null;
      }),
    ).then((results) => {
      if (cancelled) return;
      const resolved = results.filter((r): r is { uid: number; name: string } => r !== null);
      if (resolved.length > 0) {
        setNameLookup((prev) => {
          const next = { ...prev };
          resolved.forEach(({ uid, name }) => {
            next[uid] = name;
          });
          return next;
        });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [dashboardData.staffPerformance, dashboardData.recentErrors, nameLookup]);

  const handleRefresh = async () => {
    // Only refresh if projectId is available
    if (projectId === undefined || projectId === null) {
      setRefreshing(false);
      return;
    }

    setRefreshing(true);
    try {
      // Refresh all dashboard data
      await CollectDashboardAPIs.remote_refreshCollectDashboardData();

      // Then fetch the updated data with current filters
      const filters: Partial<CollectDashboardFilters> = {
        formId: selectedForm !== 'all' ? selectedForm : undefined,
        collectorId: selectedCollector !== -1 ? selectedCollector.toString() : undefined,
        projectId: projectId,
      };

      // Handle time range filtering
      if (timeRange === 'custom' && customDateRange) {
        // Use custom date range
        filters.startDate = customDateRange[0];
        filters.endDate = customDateRange[1];
      } else if (timeRange !== 'all') {
        // Use predefined time range
        filters.timeRange = timeRange;
      }

      // Fetch the complete dashboard data with the filters
      const updatedData = await CollectDashboardAPIs.remote_getCollectDashboardData(filters);

      // Ensure error rates are properly handled - no default value for -1 since that's a valid value
      setDashboardData({
        ...updatedData,
        overview: {
          ...updatedData.overview,
          // No need to default errorRate to 0 since -1 is a valid value indicating no data
        },
      });
    } catch (error) {
      console.error('Error refreshing dashboard data:', error);
    } finally {
      setRefreshing(false);
    }
  };

  // Set up automatic refresh polling every 5 minutes
  useEffect(() => {
    if (projectId === undefined || projectId === null) {
      return; // Don't start polling if no projectId is available
    }

    // Set up interval to refresh data every 5 minutes (300000 ms)
    const intervalId = setInterval(
      async () => {
        try {
          // Create filters object based on current state
          const filters: Partial<CollectDashboardFilters> = {
            formId: selectedForm !== 'all' ? selectedForm : undefined,
            collectorId: selectedCollector !== -1 ? selectedCollector.toString() : undefined,
            projectId: projectId,
          };

          // Only add timeRange to filters if it's not 'all'
          if (timeRange !== 'all') {
            filters.timeRange = timeRange;
          }

          // Fetch the complete dashboard data with the filters
          const updatedData = await CollectDashboardAPIs.remote_getCollectDashboardData(filters);

          // Update the dashboard data with the fetched data
          setDashboardData(updatedData);
        } catch (error) {
          console.error('Error during automatic refresh:', error);
        }
      },
      5 * 60 * 1000,
    ); // 5 minutes in milliseconds

    // Clean up the interval when the component unmounts or dependencies change
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [projectId, selectedForm, selectedCollector, timeRange]);

  const getStatusColor = (value: number, type: string) => {
    // Return gray color if value is -1 (no data available)
    if (value === -1) return '#94a3b8'; // gray

    switch (type) {
      case 'completion':
        if (value >= 80) return '#22c55e'; // green
        if (value >= 50) return '#f59e0b'; // yellow
        return '#ef4444'; // red
      case 'error':
        if (value <= 3) return '#22c55e'; // green
        if (value <= 5) return '#f59e0b'; // yellow
        return '#ef4444'; // red
      default:
        return '#3b82f6'; // blue
    }
  };

  const getTrendIcon = (trend: string) => {
    return trend === 'up' ? '↑' : '↓';
  };

  // Function to handle form click for drill-down
  const handleFormClick = (formName: string) => {
    // Set drill-down view to form level
    setDrillDownView({ type: 'form', id: formName });

    // Update the form filter to show only the selected form
    setSelectedForm(formName);
  };

  // Function to handle collector click for drill-down
  const handleCollectorClick = (collectorId: string | number) => {
    // Set drill-down view to collector level
    setDrillDownView({ type: 'collector', id: collectorId });

    // Update the collector filter to show only the selected collector
    setSelectedCollector(collectorId.toString());
  };

  // Function to reset drill-down view
  const handleResetDrillDown = () => {
    setDrillDownView({ type: null, id: null });
    setSelectedForm('all');
    setSelectedCollector('all');
  };

  // Dashboard Widget visibility & order state persisted per project
  const widgetStorageKey = useMemo(
    () => `geocollect_dashboard_widgets_config_${projectId ?? 'default'}`,
    [projectId],
  );

  const [widgetConfigs, setWidgetConfigs] = useState<DashboardWidgetConfigItem[]>(() =>
    DEFAULT_DASHBOARD_WIDGETS.map((w) => ({ ...w })),
  );

  useEffect(() => {
    const defaults = DEFAULT_DASHBOARD_WIDGETS.map((w) => ({ ...w }));
    try {
      const raw = window.localStorage.getItem(widgetStorageKey);
      if (raw) {
        const saved: Array<{ key: DashboardWidgetKey; visible: boolean }> = JSON.parse(raw);
        if (Array.isArray(saved) && saved.length > 0) {
          const defaultMap = new Map(defaults.map((item) => [item.key, item]));
          const merged: DashboardWidgetConfigItem[] = [];

          saved.forEach((s) => {
            const match = defaultMap.get(s.key);
            if (match) {
              merged.push({
                ...match,
                visible: typeof s.visible === 'boolean' ? s.visible : true,
              });
              defaultMap.delete(s.key);
            }
          });

          defaultMap.forEach((remaining) => {
            merged.push(remaining);
          });

          setWidgetConfigs(merged);
          return;
        }
      }
    } catch (err) {
      console.warn('Failed to restore dashboard widget settings from localStorage:', err);
    }

    setWidgetConfigs(defaults);
  }, [widgetStorageKey]);

  const handleChangeWidgetConfigs = useCallback(
    (nextWidgets: DashboardWidgetConfigItem[]) => {
      setWidgetConfigs(nextWidgets);
      try {
        window.localStorage.setItem(
          widgetStorageKey,
          JSON.stringify(nextWidgets.map((w) => ({ key: w.key, visible: w.visible }))),
        );
      } catch (err) {
        console.warn('Failed to persist dashboard widget settings:', err);
      }
    },
    [widgetStorageKey],
  );

  const handleResetWidgetConfigs = useCallback(() => {
    const defaults = DEFAULT_DASHBOARD_WIDGETS.map((w) => ({ ...w }));
    setWidgetConfigs(defaults);
    try {
      window.localStorage.removeItem(widgetStorageKey);
    } catch {
      // ignore
    }
  }, [widgetStorageKey]);

  const handleHideWidget = useCallback(
    (key: DashboardWidgetKey) => {
      handleChangeWidgetConfigs(
        widgetConfigs.map((w) => (w.key === key ? { ...w, visible: false } : w)),
      );
    },
    [widgetConfigs, handleChangeWidgetConfigs],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 4,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleCanvasDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (over && active.id !== over.id) {
        const oldIndex = widgetConfigs.findIndex((w) => w.key === active.id);
        const newIndex = widgetConfigs.findIndex((w) => w.key === over.id);
        if (oldIndex !== -1 && newIndex !== -1) {
          handleChangeWidgetConfigs(arrayMove(widgetConfigs, oldIndex, newIndex));
        }
      }
    },
    [widgetConfigs, handleChangeWidgetConfigs],
  );

  const visibleWidgets = useMemo(
    () => widgetConfigs.filter((w) => w.visible),
    [widgetConfigs],
  );

  const renderWidgetContent = (key: DashboardWidgetKey) => {
    switch (key) {
      case 'overview':
        return (
          <SortableWidgetCard
            key="overview"
            widgetKey="overview"
            title="Tổng quan"
            icon={<HiOutlineChartBar />}
            onHide={handleHideWidget}
          >
            <UI_KPIGrid>
              <UI_KPIItem>
                <UI_KPIValue>{dashboardData.overview.totalRecords.toLocaleString()}</UI_KPIValue>
                <UI_KPILabel>Bản ghi thu thập</UI_KPILabel>
                <UI_Sparkline><PiTrendUp style={{ fontSize: 18, color: '#1677ff' }} /></UI_Sparkline>
              </UI_KPIItem>
              <UI_KPIItem>
                <UI_KPIValue style={{ color: getStatusColor(dashboardData.overview.completionRate, 'completion') }}>
                  {dashboardData.overview.completionRate === -1 ? '-' : `${dashboardData.overview.completionRate}%`}
                </UI_KPIValue>
                <UI_KPILabel>Tỷ lệ hoàn thành</UI_KPILabel>
                <UI_Sparkline><PiChartBar style={{ fontSize: 18, color: '#52c41a' }} /></UI_Sparkline>
              </UI_KPIItem>
              <UI_KPIItem>
                <UI_KPIValue style={{ color: getStatusColor(dashboardData.overview.errorRate, 'error') }}>
                  {dashboardData.overview.errorRate === -1 ? '-' : `${dashboardData.overview.errorRate}%`}
                </UI_KPIValue>
                <UI_KPILabel>Tỷ lệ lỗi</UI_KPILabel>
                <UI_Sparkline><PiTrendDown style={{ fontSize: 18, color: '#f5222d' }} /></UI_Sparkline>
              </UI_KPIItem>
            </UI_KPIGrid>
          </SortableWidgetCard>
        );

      case 'formsProgress':
        return (
          <SortableWidgetCard
            key="formsProgress"
            widgetKey="formsProgress"
            title="Tiến độ theo Biểu mẫu"
            icon={<HiOutlineChartBar />}
            onHide={handleHideWidget}
          >
            <UI_ProgressList>
              {dashboardData.formsProgress.map((form, index) => (
                <UI_ProgressItem
                  key={index}
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    if (onFormSelect && form.formUuid) {
                      onFormSelect(form.formUuid, form);
                    }
                  }}
                >
                  <UI_ProgressInfo>
                    <UI_ProgressName>{form.name}</UI_ProgressName>
                    <UI_ProgressStats>
                      {form.target === -1
                        ? `${form.current} (chưa đặt KPI)`
                        : `${form.percentage}% (${form.current}/${form.target})`}
                    </UI_ProgressStats>
                  </UI_ProgressInfo>
                  {form.target !== -1 && (
                    <UI_ProgressBar>
                      <UI_ProgressFill
                        percentage={form.percentage}
                        color={getStatusColor(form.percentage, 'completion')}
                      />
                    </UI_ProgressBar>
                  )}
                </UI_ProgressItem>
              ))}
            </UI_ProgressList>
          </SortableWidgetCard>
        );

      case 'staffPerformance':
        return (
          <SortableWidgetCard
            key="staffPerformance"
            widgetKey="staffPerformance"
            title="Top Nhân viên"
            icon={<HiOutlineUsers />}
            onHide={handleHideWidget}
          >
            <UI_StaffTable>
              <UI_StaffHeader>
                <span>Rank</span>
                <span>Tên</span>
                <UI_StaffRecordsHeader>Số lượng thu thập</UI_StaffRecordsHeader>
              </UI_StaffHeader>
              {dashboardData.staffPerformance.slice(0, 10).map((staff, index) => {
                const userId = staff.userId || parseInt(staff.name?.replace('User ', '') || '0');
                const displayName = nameLookup[userId] || staff.name;

                return (
                  <UI_StaffRow
                    key={staff.rank}
                    hasError={staff.errors !== -1 && (staff.errors > 5 || staff.completion < 70)}
                    style={{ cursor: 'pointer' }}
                    title={`Phóng tới vị trí của ${displayName} trên bản đồ`}
                    onClick={() => {
                      if (userId > 0) {
                        dispatchZoomToMember({ userId, fullname: displayName });
                      }
                    }}
                  >
                    <span>#{index + 1}</span>
                    <span>{displayName}</span>
                    <UI_StaffRecordsValue>{staff.records.toLocaleString()}</UI_StaffRecordsValue>
                  </UI_StaffRow>
                );
              })}
            </UI_StaffTable>
          </SortableWidgetCard>
        );

      case 'dailyTrend':
        return (
          <SortableWidgetCard
            key="dailyTrend"
            widgetKey="dailyTrend"
            title="Xu hướng thu thập theo ngày"
            icon={<HiOutlineTrendingUp />}
            onHide={handleHideWidget}
          >
            <DailyCollectionTrendChart dashboardData={dashboardData} />
          </SortableWidgetCard>
        );

      case 'staffShare':
        return (
          <SortableWidgetCard
            key="staffShare"
            widgetKey="staffShare"
            title="Tỷ trọng đóng góp Nhân viên"
            icon={<HiOutlineChartPie />}
            onHide={handleHideWidget}
          >
            <StaffContributionDonutChart
              dashboardData={dashboardData}
              nameLookup={nameLookup}
            />
          </SortableWidgetCard>
        );

      case 'activityBreakdown':
        return (
          <SortableWidgetCard
            key="activityBreakdown"
            widgetKey="activityBreakdown"
            title="Phân bố Hình học & Khung giờ"
            icon={<HiOutlineClock />}
            onHide={handleHideWidget}
          >
            <GeometryAndHourlyChart dashboardData={dashboardData} />
          </SortableWidgetCard>
        );

      default:
        return null;
    }
  };

  const FilterContent = () => (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <UI_FilterGroup>
        <UI_FilterLabel>Biểu mẫu:</UI_FilterLabel>
        <Select
          value={selectedForm}
          onChange={setSelectedForm}
          size="small"
          style={{ width: '100%' }}
          options={[
            { label: 'Tất cả', value: 'all' },
            { label: 'Khảo sát CSKH', value: 'survey' },
            { label: 'Kiểm tra kho', value: 'warehouse' },
            { label: 'Thu thập hiện trường', value: 'field' },
          ]}
        />
      </UI_FilterGroup>

      <UI_FilterGroup>
        <UI_FilterLabel>Nhân viên:</UI_FilterLabel>
        <GeocollectMembersSelector
          showAllOption={true}
          allOptionLabel="Tất cả"
          value={selectedCollector}
          onChange={setSelectedCollector}
          style={{ width: '100%' }}
          placeholder="Chọn nhân viên..."
        />
      </UI_FilterGroup>

      <UI_FilterGroup>
        <UI_FilterLabel>Thời gian:</UI_FilterLabel>
        <Select
          value={timeRange}
          onChange={setTimeRange}
          size="small"
          style={{ width: '100%' }}
          options={[
            { label: 'Tất cả', value: 'all' },
            { label: 'Hôm nay', value: 'today' },
            { label: '3 ngày', value: '3days' },
            { label: '7 ngày', value: '7days' },
            { label: 'Tháng này', value: 'this_month' },
            { label: 'Tùy chỉnh', value: 'custom' },
          ]}
        />
      </UI_FilterGroup>
    </Space>
  );

  return (
    <UI_Container>
      {/* Header with controls */}
      <UI_Header>
        <UI_HeaderLeft>
          <UI_Title>
            Dashboard
            {drillDownView.type && (
              <span style={{ marginLeft: '10px', fontSize: '12px', color: '#64748b' }}>
                → {drillDownView.type === 'form' ? `Form: ${drillDownView.id}` : `Collector: ${drillDownView.id}`}
              </span>
            )}
          </UI_Title>
        </UI_HeaderLeft>
        <UI_HeaderRight>
          <Space size="small">
            {drillDownView.type && (
              <Tooltip title="Reset view">
                <Button icon={<HiOutlineEye />} size="small" onClick={handleResetDrillDown} type="text" />
              </Tooltip>
            )}
            <UI_MobileFilterButton>
              <Tooltip title="Bộ lọc">
                <Button icon={<HiOutlineFilter />} size="small" onClick={() => setShowFiltersModal(true)} type="text" />
              </Tooltip>
            </UI_MobileFilterButton>
            <Tooltip title="Tải xuống báo cáo">
              <Button icon={<DownloadOutlined />} size="small" type="text" />
            </Tooltip>
            <Tooltip title="Làm mới">
              <Button icon={<ReloadOutlined />} size="small" onClick={handleRefresh} loading={refreshing} type="text" />
            </Tooltip>
            <DashboardWidgetSettingsPopover
              widgets={widgetConfigs}
              onChangeWidgets={handleChangeWidgetConfigs}
              onResetWidgets={handleResetWidgetConfigs}
            />
            <Tooltip title={isMaximized ? 'Thu nhỏ' : 'Phóng to'}>
              <Button
                icon={isMaximized ? <CompressOutlined /> : <ExpandOutlined />}
                size="small"
                onClick={onMaximize}
                type="text"
              />
            </Tooltip>
            <Tooltip title="Đóng">
              <Button icon={<CloseOutlined />} size="small" onClick={onClose} type="text" />
            </Tooltip>
          </Space>
        </UI_HeaderRight>
      </UI_Header>

      {/* Quick Filters - Desktop only */}
      <UI_Filters>
        <Space size="middle" wrap>
          <UI_FilterGroup>
            <UI_FilterLabel>Biểu mẫu:</UI_FilterLabel>
            <Select
              value={selectedForm}
              onChange={setSelectedForm}
              size="small"
              style={{ minWidth: 120 }}
              options={[
                { label: 'Tất cả', value: 'all' },
                { label: 'Khảo sát CSKH', value: 'survey' },
                { label: 'Kiểm tra kho', value: 'warehouse' },
                { label: 'Thu thập hiện trường', value: 'field' },
              ]}
            />
          </UI_FilterGroup>

          <UI_FilterGroup>
            <UI_FilterLabel>Nhân viên:</UI_FilterLabel>
            <GeocollectMembersSelector
              showAllOption={true}
              allOptionLabel="Tất cả"
              value={selectedCollector}
              onChange={setSelectedCollector}
              style={{ minWidth: 120 }}
              placeholder="Chọn nhân viên..."
            />
          </UI_FilterGroup>

          <UI_FilterGroup>
            <UI_FilterLabel>Thời gian:</UI_FilterLabel>
            <Select
              value={timeRange}
              onChange={setTimeRange}
              size="small"
              style={{ minWidth: 120 }}
              options={[
                { label: 'Tất cả', value: 'all' },
                { label: 'Hôm nay', value: 'today' },
                { label: '3 ngày', value: '3days' },
                { label: '7 ngày', value: '7days' },
                { label: 'Tháng này', value: 'this_month' },
                { label: 'Tùy chỉnh', value: 'custom' },
              ]}
            />
          </UI_FilterGroup>
        </Space>
      </UI_Filters>

      {/* Mobile Filters Modal */}
      <Modal
        title="Bộ lọc"
        open={showFiltersModal}
        onCancel={() => setShowFiltersModal(false)}
        footer={[
          <Button key="cancel" onClick={() => setShowFiltersModal(false)}>
            Hủy
          </Button>,
          <Button key="apply" type="primary" onClick={() => setShowFiltersModal(false)}>
            Áp dụng
          </Button>,
        ]}
        width={320}
      >
        <FilterContent />
      </Modal>

      {/* Dashboard Content */}
      <UI_DashboardContent style={{ height: tableHeight }}>
        {visibleWidgets.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleCanvasDragEnd}
          >
            <SortableContext
              items={visibleWidgets.map((w) => w.key)}
              strategy={rectSortingStrategy}
            >
              <UI_TopCardsContainer data-testid="dashboard-widgets-container">
                {visibleWidgets.map((w) => renderWidgetContent(w.key))}
              </UI_TopCardsContainer>
            </SortableContext>
          </DndContext>
        ) : (
          <UI_EmptyWidgetsState data-testid="dashboard-empty-widgets">
            <UI_EmptyWidgetsText>Tất cả các widget đã được ẩn khỏi Dashboard.</UI_EmptyWidgetsText>
            <Button
              size="small"
              type="primary"
              onClick={handleResetWidgetConfigs}
              data-testid="btn-restore-default-widgets"
            >
              Khôi phục mặc định
            </Button>
          </UI_EmptyWidgetsState>
        )}

        {/* Bottom Row */}
        <UI_BottomCardsContainer style={{ display: 'none' }}>
          {/* Error Heatmap */}
          <UI_HeatmapCard>
            <UI_CardTitle>
              <FaTriangleExclamation />
              Lỗi theo Biểu mẫu × Field
            </UI_CardTitle>
            <UI_Heatmap>
              <UI_HeatmapTable>
                <thead>
                  <tr>
                    <th></th>
                    {dashboardData.errorHeatmap.fields.map((field) => (
                      <th key={field}>{field}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dashboardData.errorHeatmap.forms.map((form, i) => (
                    <tr key={form}>
                      <td onClick={() => handleFormClick(form)} style={{ cursor: 'pointer' }}>
                        {form}
                      </td>
                      {dashboardData.errorHeatmap.data[i].map((value, j) => (
                        <td key={j}>
                          <UI_HeatmapCell intensity={value}>{value === -1 ? '-' : `${value}%`}</UI_HeatmapCell>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </UI_HeatmapTable>
            </UI_Heatmap>
          </UI_HeatmapCard>

          {/* Recent Errors */}
          <UI_ErrorsCard>
            <UI_CardTitle>
              <FaTriangleExclamation />
              Lỗi gần đây
            </UI_CardTitle>
            <UI_ErrorsList>
              {dashboardData.recentErrors.map((error, index) => {
                // Extract user ID from error record to look up real name
                const userId = error.userId || parseInt(error.collector?.replace('User ', '') || '0');
                const displayName = nameLookup[userId] || error.collector;

                return (
                  <UI_ErrorItem key={index}>
                    <UI_ErrorTime>{error.time}</UI_ErrorTime>
                    <UI_ErrorDetails>
                      <UI_ErrorUser onClick={() => handleCollectorClick(displayName)} style={{ cursor: 'pointer' }}>
                        {displayName}
                      </UI_ErrorUser>
                      <UI_ErrorForm onClick={() => handleFormClick(error.form)} style={{ cursor: 'pointer' }}>
                        {error.form} → {error.field}
                      </UI_ErrorForm>
                      <UI_ErrorNote>{error.note}</UI_ErrorNote>
                    </UI_ErrorDetails>
                  </UI_ErrorItem>
                );
              })}
            </UI_ErrorsList>
          </UI_ErrorsCard>
        </UI_BottomCardsContainer>
      </UI_DashboardContent>
    </UI_Container>
  );
};

export default memo(DashboardTab);

